import { defineStore } from 'pinia'
import { ref } from 'vue'

import { GolfClubService } from '@/services/admin'
import type { IGolfclub } from '@/types'
import type { IGolfclubGeojsonFeatureCollection } from '@/types/golfclub-geojson'

export const useGolfStore = defineStore(
  'golf-store',
  () => {
    const golfclub = ref<IGolfclub | null>(null)
    const geojson = ref<IGolfclubGeojsonFeatureCollection | null>(null)
    // `checksum` the cached `geojson` was fetched under. Unlike `updated_at` (which bumps on any
    // club field edit), `checksum` only changes when geojson-relevant data (holes/pins/courses) does.
    const cachedGeojsonChecksum = ref<string | null>(null)

    /**
     * Sets the golf club information by fetching it from the service.
     * @param id - The ID of the golf club.
     */
    async function setGolfClubInfo(id: number) {
      // `geojson` is persisted and belongs to the club it was fetched for, so a switch has to drop
      // it — otherwise getClubGeoJSON keeps serving the previous club's course from the cache.
      if (golfclub.value?.id !== id) {
        geojson.value = null
      }
      golfclub.value = await GolfClubService.getSingle(id)
      localStorage.setItem('gc', id.toString())
    }

    /**
     * Fetches the GeoJSON data for a golf club by its ID.
     * @param id - The ID of the golf club.
     * @param apiChecksum - The golf club's current `checksum`, as just fetched from the API. If it
     *   differs from `cachedGeojsonChecksum`, the cache is treated as stale and re-fetched.
     * @param force - If true, forces a fresh fetch even if cached data exists.
     */
    async function getClubGeoJSON(
      id: number,
      apiChecksum?: string | null,
      force: boolean = false,
    ): Promise<IGolfclubGeojsonFeatureCollection | null> {
      // Stale if forced, or the underlying geojson data changed in the DB since this was fetched.
      const isStale = apiChecksum != null && apiChecksum !== cachedGeojsonChecksum.value
      if (force || isStale) {
        geojson.value = null
      }

      // Return cached GeoJSON if available
      if (geojson.value) {
        return geojson.value
      }

      // Fetch GeoJSON if not cached
      try {
        const clubGeojsonRes = await GolfClubService.getGeojson(id)
        const clubGeojsonUrl = clubGeojsonRes.url
        const geojsonData = await fetch(clubGeojsonUrl).then((res) => res.json())
        geojson.value = geojsonData
        cachedGeojsonChecksum.value = apiChecksum ?? null
        return geojsonData
      } catch (error) {
        console.error('Error fetching golf club GeoJSON:', error)
        return null
      }
    }

    return { golfclub, geojson, cachedGeojsonChecksum, setGolfClubInfo, getClubGeoJSON }
  },
  {
    persist: true,
  },
)
